import { useState, type FormEvent, type ReactNode } from 'react'
import { resetPassword } from '../../core/index.js'
import { Button, PasswordInput } from '../../core/index.js'
import { KeyRound } from 'lucide-react'

function AuthCard({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-marine-lt px-4">
      <div className="w-full max-w-sm bg-white border border-marine/15 p-8 shadow-sm">
        <div className="text-[9px] font-bold uppercase tracking-[2.5px] text-coral mb-1">autonoME</div>
        {children}
      </div>
    </div>
  )
}

function getTokenFromQuery(): string | null {
  return new URLSearchParams(window.location.search).get('token')
}

export interface ResetPasswordProps {
  /** Where "Go to sign in" leads: the app's sign-in, or the admin area's (the admin's reset link opens `/admin/reset-password`). */
  signInPath?: string
}

/**
 * Completes a reset: from a link's token, or - opened without one, since
 * the self-service reset (invitations plan phase 4) - from the email the
 * request was made for and the six-digit code it was sent.
 */
export function ResetPassword({ signInPath = '/' }: ResetPasswordProps) {
  const [token] = useState(getTokenFromQuery)
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle')
  const [error, setError] = useState<string | null>(null)

  if (status === 'success') {
    return (
      <AuthCard>
        <h1 className="text-xl font-black text-ink mb-4">Password updated</h1>
        <p className="text-sm text-ink-mid mb-6">You can sign in with your new password now.</p>
        <a href={signInPath} className="text-sm font-bold text-marine hover:text-marine-dk">
          Go to sign in
        </a>
      </AuthCard>
    )
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault()
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setStatus('submitting')
    setError(null)
    try {
      await resetPassword(token ? { token } : { email: email.trim(), code: code.trim() }, newPassword)
      setStatus('success')
    } catch (err) {
      setStatus('idle')
      setError(err instanceof Error ? err.message : 'Failed to reset password')
    }
  }

  return (
    <AuthCard>
      <form onSubmit={handleSubmit}>
        <h1 className="text-xl font-black text-ink mb-6">Set a new password</h1>

        {!token && (
          <>
            <p className="text-sm text-ink-mid mb-4">Enter the email you asked the reset for and the six-digit code it was sent.</p>
            <label className="block text-xs font-bold text-ink-mid mb-1" htmlFor="resetEmail">
              Email
            </label>
            <input id="resetEmail" type="email" className="w-full border border-marine/25 px-3 py-2 mb-4 text-sm text-ink focus:outline-none focus:border-marine" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="username" required />
            <label className="block text-xs font-bold text-ink-mid mb-1" htmlFor="resetCode">
              Code
            </label>
            <input id="resetCode" inputMode="numeric" pattern="\d{6}" maxLength={6} className="w-full border border-marine/25 px-3 py-2 mb-4 text-sm text-ink focus:outline-none focus:border-marine" value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} placeholder="123456" required />
          </>
        )}

        <label className="block text-xs font-bold text-ink-mid mb-1" htmlFor="newPassword">
          New password
        </label>
        <PasswordInput
          id="newPassword"
          className="w-full border border-marine/25 px-3 py-2 mb-4 text-sm text-ink focus:outline-none focus:border-marine"
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          autoComplete="new-password"
          minLength={8}
          autoFocus={Boolean(token)}
          required
        />

        <label className="block text-xs font-bold text-ink-mid mb-1" htmlFor="confirmPassword">
          Confirm new password
        </label>
        <PasswordInput
          id="confirmPassword"
          className="w-full border border-marine/25 px-3 py-2 mb-4 text-sm text-ink focus:outline-none focus:border-marine"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          autoComplete="new-password"
          minLength={8}
          required
        />

        {error && <div className="text-coral text-xs font-bold mb-4">{error}</div>}

        <Button icon={KeyRound} size="lg" fullWidth type="submit" disabled={status === 'submitting' || (!token && code.length !== 6)}>
          {status === 'submitting' ? 'Saving…' : 'Set new password'}
        </Button>
      </form>
    </AuthCard>
  )
}
